import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  message,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  CloudUploadOutlined,
  InboxOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { GetAllUsers, Task } from "../firebase-config";
import { ApplyXpImport } from "../firebase-xp-import";
import { useAuth } from "../contexts/AuthContext";
import {
  buildXpImportPlan,
  findUsersMissingFromExcel,
  NOTA_APROBACION,
  ParseResult,
  parseWorkbook,
  RawSheet,
  SheetReport,
  XP_AREAS,
  XP_POR_EVENTO,
  XpPlanItem,
  XpPlanStatus,
} from "../utils/xpImport";

const STATUS_META: Record<XpPlanStatus, { label: string; color: string }> = {
  update: { label: "Se actualiza", color: "green" },
  unchanged: { label: "Sin cambios", color: "default" },
  not_found: { label: "No existe en la app", color: "orange" },
  invalid: { label: "Con errores", color: "red" },
};

type StatusFilter = "all" | XpPlanStatus;

interface XpImportTabProps {
  /** Se llama después de aplicar, para refrescar la tabla de Usuarios. */
  onApplied?: () => void;
}

/** Lee TODAS las hojas del archivo (una por materia). */
const readWorkbook = async (file: File): Promise<RawSheet[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: "",
    });
    const headers = ((matrix[0] ?? []) as unknown[])
      .map((h) => String(h ?? "").trim())
      .filter(Boolean);
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: "",
    });
    return { name, headers, rows };
  });
};

/** Texto corto con lo que se encontró en cada hoja, para revisar de un vistazo. */
const describeSheet = (r: SheetReport) => {
  if (r.skipped) return "Sin columnas de notas: se ignora.";
  if (r.error) return r.error;
  const parts = [
    `${r.actividades} actividades`,
    r.tieneParcial1 ? `parcial 1${r.tieneRecuperatorio1 ? " + recup." : ""}` : "sin parcial 1",
    r.tieneParcial2 ? `parcial 2${r.tieneRecuperatorio2 ? " + recup." : ""}` : "sin parcial 2",
    r.tieneFinal ? "final" : "sin final",
  ];
  return `${parts.join(", ")}. ${r.alumnos} alumnos.`;
};

const XpImportTab: React.FC<XpImportTabProps> = ({ onApplied }) => {
  const { user } = useAuth();
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [users, setUsers] = useState<Task[]>([]);
  const [reading, setReading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [keepManualXp, setKeepManualXp] = useState(true);
  const [notifyUsers, setNotifyUsers] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("all");

  const plan: XpPlanItem[] = useMemo(
    () =>
      parsed
        ? buildXpImportPlan(parsed.rows, users, {
            keepManualXpOnFirstImport: keepManualXp,
          })
        : [],
    [parsed, users, keepManualXp],
  );

  const counts = useMemo(() => {
    const base: Record<XpPlanStatus, number> = {
      update: 0,
      unchanged: 0,
      not_found: 0,
      invalid: 0,
    };
    plan.forEach((item) => (base[item.status] += 1));
    return base;
  }, [plan]);

  const missingFromExcel = useMemo(
    () => (parsed ? findUsersMissingFromExcel(parsed.rows, users) : []),
    [parsed, users],
  );

  // Columnas de área: las configuradas + cualquier hoja sin mapear.
  const areaNames = useMemo(() => {
    const names = new Set(XP_AREAS.map((a) => a.skillLabel));
    parsed?.sheets.forEach((s) => {
      if (!s.skipped && !s.error) s.areas.forEach((a) => names.add(a.area));
    });
    return Array.from(names);
  }, [parsed]);

  const visibleItems = useMemo(
    () =>
      filter === "all" ? plan : plan.filter((item) => item.status === filter),
    [plan, filter],
  );

  const handleFile = async (file: File) => {
    setReading(true);
    try {
      const [sheets, freshUsers] = await Promise.all([
        readWorkbook(file),
        // Siempre usuarios frescos: el plan compara contra lo que hay hoy.
        GetAllUsers(),
      ]);
      setUsers(freshUsers);
      setParsed(parseWorkbook(sheets));
      setFileName(file.name);
      setFilter("all");
    } catch (error) {
      console.error(error);
      message.error(
        "No se pudo leer el archivo. Revisá que sea un .xlsx, .xls o .csv.",
      );
    } finally {
      setReading(false);
    }
    return false; // evita que antd intente subir el archivo a una URL
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const result = await ApplyXpImport({
        items: plan,
        fileName,
        adminEmail: user?.email ?? "",
        notifyUsers,
      });
      message.success(`XP actualizada para ${result.updated} usuarios.`);
      if (!result.logSaved) {
        message.warning(
          "La XP se actualizó, pero no se guardó el registro. Revisá la regla de ImportacionesXP.",
        );
      }
      // Recalculamos contra Firestore: todo debería quedar "Sin cambios".
      setUsers(await GetAllUsers());
      onApplied?.();
    } catch (error) {
      console.error(error);
      message.error(
        "No se pudo aplicar la importación. Revisá que tu usuario tenga permisos de admin en las reglas de Firestore.",
      );
    } finally {
      setApplying(false);
    }
  };

  const reset = () => {
    setParsed(null);
    setFileName("");
    setUsers([]);
  };

  const columns = [
    {
      title: "Usuario",
      key: "usuario",
      render: (_: unknown, i: XpPlanItem) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>
            {i.user?.Nombre || i.row.nombre || "—"}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {i.row.email || "(sin correo)"}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "XP",
      key: "xp",
      render: (_: unknown, i: XpPlanItem) => {
        if (!i.before || !i.after) return i.row.totalExp;
        const delta = i.deltaExp;
        return (
          <Space direction="vertical" size={0}>
            <span>
              {i.before.currentExp} → <strong>{i.after.currentExp}</strong>
            </span>
            {delta !== 0 ? (
              <Typography.Text
                style={{ fontSize: 12, color: delta > 0 ? "#2f9e44" : "#e03131" }}
              >
                {delta > 0 ? `+${delta}` : delta}
              </Typography.Text>
            ) : null}
            {i.extraXp > 0 ? (
              <Tooltip title="XP de eventos, misiones o carga manual que se conserva por encima del Excel.">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  incluye {i.extraXp} extra
                </Typography.Text>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: "Nivel",
      key: "nivel",
      width: 90,
      render: (_: unknown, i: XpPlanItem) =>
        i.before && i.after ? (
          i.before.nivel === i.after.nivel ? (
            i.after.nivel
          ) : (
            <span>
              {i.before.nivel} → <strong>{i.after.nivel}</strong>
            </span>
          )
        ) : (
          "—"
        ),
    },
    ...areaNames.map((area) => ({
      title: area,
      key: `area-${area}`,
      width: 110,
      render: (_: unknown, i: XpPlanItem) => i.row.areas[area] ?? 0,
    })),
    {
      title: "Estado",
      key: "estado",
      width: 170,
      render: (_: unknown, i: XpPlanItem) => {
        const meta = STATUS_META[i.status];
        const notes = [...i.row.errors, ...i.row.warnings];
        return (
          <Space size={4}>
            <Tag color={meta.color}>{meta.label}</Tag>
            {notes.length ? (
              <Tooltip title={notes.join(" ")}>
                <WarningOutlined
                  style={{ color: i.row.errors.length ? "#e03131" : "#f08c00" }}
                  aria-label="Ver avisos de la fila"
                />
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Typography.Text style={{ color: "var(--dashboard-text)" }}>
        Subí el Excel de notas del campus, con una hoja por materia. Cada
        actividad (ARO) aprobada con {NOTA_APROBACION} o más suma{" "}
        {XP_POR_EVENTO.actividad} XP; el parcial 1, {XP_POR_EVENTO.parcial1};
        el parcial 2, {XP_POR_EVENTO.parcial2}; y el final,{" "}
        {XP_POR_EVENTO.final}. Un parcial aprobado en el recuperatorio cuenta
        igual, una sola vez. Antes de guardar vas a ver qué cambia en cada
        usuario, y podés subir el mismo archivo más de una vez sin duplicar XP.
      </Typography.Text>

      {!parsed ? (
        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          multiple={false}
          showUploadList={false}
          beforeUpload={(file) => handleFile(file as File)}
          disabled={reading}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {reading ? "Leyendo planilla…" : "Arrastrá el Excel acá o hacé clic para elegirlo"}
          </p>
          <p className="ant-upload-hint">
            Se leen las columnas de correo, ARO 1 a 8, parciales,
            recuperatorios y final de cada hoja.
          </p>
        </Upload.Dragger>
      ) : (
        <>
          <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
            <Typography.Text strong>{fileName}</Typography.Text>
            <Button onClick={reset}>Elegir otra planilla</Button>
          </Space>

          <Table
            size="small"
            pagination={false}
            rowKey="sheet"
            dataSource={parsed.sheets}
            columns={[
              { title: "Materia (hoja)", dataIndex: "sheet", key: "sheet" },
              {
                title: "Suma a",
                key: "area",
                render: (_: unknown, r: SheetReport) =>
                  r.skipped || r.error ? "—" : (
                    <Tooltip
                      title={
                        r.unmappedArea
                          ? "Esta materia no está en MATERIA_A_AREA (utils/xpImport.ts): se crea una skill con su nombre."
                          : undefined
                      }
                    >
                      <Space size={4} wrap>
                        {r.areas.map((a) => (
                          <Tag key={a.area} color={r.unmappedArea ? "orange" : "purple"}>
                            {a.area}
                            {a.share < 1 ? ` ${Math.round(a.share * 100)}%` : ""}
                          </Tag>
                        ))}
                      </Space>
                    </Tooltip>
                  ),
              },
              {
                title: "Qué se encontró",
                key: "detalle",
                render: (_: unknown, r: SheetReport) => (
                  <Typography.Text type={r.error ? "danger" : undefined}>
                    {describeSheet(r)}
                  </Typography.Text>
                ),
              },
            ]}
          />

          {parsed.fatalError ? (
            <Alert
              type="error"
              showIcon
              message="No se pudo usar este archivo"
              description={`${parsed.fatalError} Revisá que sea la exportación de notas del campus.`}
            />
          ) : (
            <>
              <Space wrap size={8}>
                {(Object.keys(STATUS_META) as XpPlanStatus[]).map((status) => (
                  <Tag key={status} color={STATUS_META[status].color}>
                    {counts[status]} {STATUS_META[status].label.toLowerCase()}
                  </Tag>
                ))}
              </Space>

              {counts.not_found ? (
                <Alert
                  type="warning"
                  showIcon
                  message={`${counts.not_found} correos del Excel no tienen cuenta en la app`}
                  description="Puede ser un error de tipeo o un alumno que todavía no se creó desde Unity. Esas filas se saltean; el resto se importa igual."
                />
              ) : null}

              <Space direction="vertical" size={8}>
                <Space>
                  <Switch checked={keepManualXp} onChange={setKeepManualXp} />
                  <Typography.Text>
                    En la primera importación de cada usuario, conservar la XP
                    que tenga por encima del Excel (eventos, misiones o carga
                    manual)
                  </Typography.Text>
                </Space>
                <Space>
                  <Switch checked={notifyUsers} onChange={setNotifyUsers} />
                  <Typography.Text>
                    Enviar una notificación a los usuarios cuya XP cambia
                  </Typography.Text>
                </Space>
              </Space>

              <Segmented<StatusFilter>
                value={filter}
                onChange={setFilter}
                options={[
                  { value: "all", label: `Todos (${plan.length})` },
                  { value: "update", label: `Se actualizan (${counts.update})` },
                  { value: "not_found", label: `No existen (${counts.not_found})` },
                  { value: "invalid", label: `Con errores (${counts.invalid})` },
                ]}
              />

              <Table
                rowKey={(i: XpPlanItem) => i.row.email || i.row.nombre}
                columns={columns as any}
                expandable={{
                  rowExpandable: (i: XpPlanItem) => i.row.materias.length > 0,
                  expandedRowRender: (i: XpPlanItem) => (
                    <Space direction="vertical" size={4}>
                      {i.row.materias.map((m) => (
                        <Typography.Text key={m.materia}>
                          <strong>{m.materia}</strong>: {m.actividadesAprobadas}/
                          {m.actividadesTotales} actividades
                          {m.parcial1 ? ", parcial 1" : ""}
                          {m.parcial2 ? ", parcial 2" : ""}
                          {m.final ? ", final" : ""} → {m.xp} XP
                          {Object.keys(m.reparto).length > 1
                            ? ` (${Object.entries(m.reparto)
                                .map(([area, v]) => `${v} a ${area}`)
                                .join(", ")})`
                            : ""}
                        </Typography.Text>
                      ))}
                    </Space>
                  ),
                }}
                dataSource={visibleItems}
                pagination={{ pageSize: 15 }}
                scroll={{ x: true }}
                size="small"
              />

              {missingFromExcel.length ? (
                <Collapse
                  items={[
                    {
                      key: "faltantes",
                      label: `${missingFromExcel.length} usuarios de la app no aparecen en el Excel (no se tocan)`,
                      children: (
                        <Space wrap>
                          {missingFromExcel.map((u) => (
                            <Tag key={u.id}>{u.Email}</Tag>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                />
              ) : null}

              <Popconfirm
                title={`¿Actualizar la XP de ${counts.update} usuarios?`}
                description="Se escribe en Firestore y queda registrado en ImportacionesXP."
                okText="Actualizar XP"
                cancelText="Cancelar"
                onConfirm={() => void handleApply()}
                disabled={!counts.update}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<CloudUploadOutlined />}
                  loading={applying}
                  disabled={!counts.update}
                >
                  {counts.update
                    ? `Actualizar XP de ${counts.update} usuarios`
                    : "No hay cambios para aplicar"}
                </Button>
              </Popconfirm>
            </>
          )}
        </>
      )}
    </Space>
  );
};

export default XpImportTab;
